/**
 * OSFI's vocabulary (P415).
 *
 * SOURCED FROM THE GUIDELINE, not paraphrased from SAMA's. E-21 says
 * *critical operation*, *tolerance for disruption* and *federally
 * regulated financial institution*; it does not say bank, because the
 * institutions OSFI supervises include insurers and pension plans, and
 * a Canadian insurer reading "bank" on every screen is being told the
 * product was not built for them.
 *
 * Two entries are deliberate refusals rather than translations.
 *
 * `mvbTerm`: E-21 does NOT require a pre-declared minimum set held warm.
 * The MVB construct goes further than the guideline asks. So the term
 * describes what the concept is rather than implying OSFI demanded it
 * -- the same labelling discipline `docs/roadmap.md` applies to
 * forward-looking capability, where an unlabelled aspiration is a
 * reportable error.
 *
 * `admissionPathway.ces` / `.ids`: those are SAMA's two streams. OSFI
 * has one pathway. Saying so is more useful than leaving a SAMA word
 * on a Canadian screen.
 *
 * French is `markUntranslated` scaffold. OSFI publishes E-21 in both
 * official languages and P425 is where the real French lands, from
 * OSFI's own text -- we do not translate a regulator's terms ourselves
 * (the P294-P298 rule: always fall towards the regulator's statement).
 */
import { markUntranslated } from "shared/i18n/markUntranslated";

import type { RegimeVocab, RegimeVocabByLanguage } from "shared/i18n/regime/types";

const en: RegimeVocab = {
  serviceTerm: "critical operation",
  serviceTermPlural: "critical operations",
  serviceTermShort: "critical operation",
  serviceTermShortPlural: "critical operations",
  serviceTermShortA: "a critical operation",
  serviceTermTitle: "Critical operation",
  serviceTermPluralTitle: "Critical operations",
  serviceTermDefinition:
    "An operation whose failure would have a material adverse impact on " +
    "the institution's financial or operational viability, its " +
    "reputation, or the safety and soundness of the financial system.",

  toleranceTerm: "tolerance for disruption",
  toleranceTermPlural: "tolerances for disruption",
  // E-21 requires risk limits to sit INSIDE the tolerance. SAMA calls
  // the same space headroom; OSFI names the limit itself.
  toleranceTermTitle: "Tolerance for disruption",
  riskLimitTerm: "risk limit",

  institutionTerm: "institution",
  institutionTermPlural: "institutions",
  institutionClassTerm: "federally regulated financial institution",
  regulatorName: "Office of the Superintendent of Financial Institutions",
  regulatorShort: "OSFI",
  examinerTerm: "supervisor",
  letterTerm: "supervisory letter",

  mvbTerm: "minimum set of operations",
  mvbTermTitle: "Minimum set of operations",
  mvbTermShort: "minimum set",

  admissionPathway: {
    ces: "not used under this regime",
    ids: "not used under this regime",
    critical_operation: "Critical operation identification",
  },

  outcomeCategory: {
    minimize_customer_harm: "Minimise impact on customers",
    retain_customer_confidence: "Retain confidence in the institution",
    regulatory_engagement: "Meet supervisory expectations",
    protect_safety_and_soundness: "Protect safety and soundness",
  },
  outcomeCategoryDefinition: {
    minimize_customer_harm:
      "Failure would cause customers or policyholders financial loss or " +
      "deny them access to their money or cover.",
    retain_customer_confidence:
      "Failure would damage confidence in the institution or in the " +
      "financial system it operates within.",
    regulatory_engagement:
      "Failure would breach a supervisory expectation or stop the " +
      "institution reporting to OSFI.",
    protect_safety_and_soundness:
      "Failure would have a material adverse impact on the institution's " +
      "viability or on the safety and soundness of the financial system.",
  },

  boardPackTitle: "Board operational resilience snapshot",
  currencySymbol: "CAD",
};

export const osfi: RegimeVocabByLanguage = {
  en,
  fr: markUntranslated(en) as RegimeVocab,
};
